import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import type { FieldError } from "react-hook-form";
import type { MonthAvailabilityMap, DayAvailabilityResult } from "../../utils/calendarAvailability";
import { toDateString } from "../../utils/calendarAvailability";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import { useWidgetMaximised } from "../context/WidgetMaximisedContext";
import { getFirstDayOfWeek, getMonthLabels, getWeekdayLabels, JS_DAY, type Weekday } from "../../utils/localeWeek";
import { resolveWidgetLocale } from "../../utils/widgetLocale";
import { RequiredIndicator } from "./requiredIndicator";

interface CalendarDatePickerProps {
  /** Unique element id for accessibility. */
  id: string;
  /** Field label text. */
  label: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Label CSS classes. */
  labelClassName?: string;
  /** Currently selected date as YYYY-MM-DD, or empty string. */
  value: string;
  /** Called when the user selects a date. */
  onChange: (dateStr: string) => void;
  /** Year currently shown in the calendar grid. */
  viewedYear: number;
  /** Month (0-indexed) currently shown in the calendar grid. */
  viewedMonth: number;
  /** Navigate to the previous month. */
  onPreviousMonth: () => void;
  /** Navigate to the next month. */
  onNextMonth: () => void;
  /** Pre-computed availability for every day in the viewed month. */
  monthAvailability: MonthAvailabilityMap;
  /** Check availability for a specific day (used for tooltip text). */
  checkDayAvailability: (dateStr: string) => DayAvailabilityResult;
  /** Earliest selectable date as YYYY-MM-DD. */
  minDate?: string;
  /** Latest selectable date as YYYY-MM-DD. */
  maxDate?: string;
  /** Validation error from react-hook-form. */
  error?: FieldError;
  /** Additional aria-describedby id(s). */
  ariaDescribedBy?: string;
  /** When true, the calendar grid is always visible inline instead of inside a popover trigger. */
  inline?: boolean;
  /** The venue's country code; sets the week start and formats the calendar in its locale when the browser reports no region. */
  venueCountryCode?: string | null;
}

/**
 * Custom calendar date picker styled as a text input trigger with a
 * popover calendar grid. Renders per-day availability; unavailable
 * days are visually greyed out and cannot be selected.
 * Fully keyboard-navigable (WCAG 2.1 AA).
 */
const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  id,
  label,
  required = false,
  labelClassName = "",
  value,
  onChange,
  viewedYear,
  viewedMonth,
  onPreviousMonth,
  onNextMonth,
  monthAvailability,
  checkDayAvailability,
  minDate,
  maxDate,
  error,
  ariaDescribedBy,
  inline = false,
  venueCountryCode,
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const locale = useMemo(() => resolveWidgetLocale(venueCountryCode), [venueCountryCode]);
  const isMaximised = useWidgetMaximised();
  const [isOpen, setIsOpen] = useState(inline);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDialogElement>(null);
  const gridRef = useRef<HTMLTableElement>(null);
  const focusedDateRef = useRef<string>(value || "");
  const firstDay = useMemo(() => getFirstDayOfWeek(venueCountryCode), [venueCountryCode]);

  // Build calendar grid data
  const weekdayLabels = useMemo(() => getWeekdayLabels(firstDay, locale), [firstDay, locale]);
  const monthLabels = useMemo(() => getMonthLabels(viewedYear, viewedMonth, locale), [viewedYear, viewedMonth, locale]);
  const weeks = buildCalendarGrid(viewedYear, viewedMonth, firstDay);

  /**
   * Full weekday and month names don't survive the width the widget is embedded
   * at, so it shows the locale's shorter forms until it is maximised. Everywhere
   * else, and for accessible names at either size, the longer forms fit.
   */
  const useCompactLabels = isWidget && !isMaximised;

  const isBeforeMin = useCallback((dateStr: string) => (minDate ? dateStr < minDate : false), [minDate]);

  const isAfterMax = useCallback((dateStr: string) => (maxDate ? dateStr > maxDate : false), [maxDate]);

  /**
   * The earliest bookable month is the one holding `minDate`, or the current
   * month when no minimum is given. Months before it hold nothing bookable,
   * so navigating back out of it is not offered.
   */
  const isAtEarliestMonth = useMemo(
    () => isMonthAtOrBefore(viewedYear, viewedMonth, minDate ?? toDateString(new Date())),
    [viewedYear, viewedMonth, minDate],
  );

  /** Mirrors `isAtEarliestMonth` for the latest bookable month; unbounded when no `maxDate` is given. */
  const isAtLatestMonth = useMemo(
    () => (maxDate ? isMonthAtOrAfter(viewedYear, viewedMonth, maxDate) : false),
    [viewedYear, viewedMonth, maxDate],
  );

  const goToPreviousMonth = useCallback(() => {
    if (!isAtEarliestMonth) onPreviousMonth();
  }, [isAtEarliestMonth, onPreviousMonth]);

  const goToNextMonth = useCallback(() => {
    if (!isAtLatestMonth) onNextMonth();
  }, [isAtLatestMonth, onNextMonth]);

  const isDayDisabled = useCallback(
    (dateStr: string): boolean => {
      if (isBeforeMin(dateStr) || isAfterMax(dateStr)) return true;
      const result = monthAvailability[dateStr];
      return result ? !result.available : true;
    },
    [monthAvailability, isBeforeMin, isAfterMax],
  );

  const getDisabledReason = useCallback(
    (dateStr: string): string | undefined => {
      if (isBeforeMin(dateStr)) return "Date is too early";
      if (isAfterMax(dateStr)) return "Date is too far ahead";
      const result = checkDayAvailability(dateStr);
      return result.available ? undefined : (result.reason ?? "Unavailable");
    },
    [isBeforeMin, isAfterMax, checkDayAvailability],
  );

  const handleDayClick = useCallback(
    (dateStr: string) => {
      if (!isDayDisabled(dateStr)) {
        onChange(dateStr);
        focusedDateRef.current = dateStr;
        setIsOpen(false);
        // Return focus to the trigger after selection
        requestAnimationFrame(() => {
          triggerRef.current?.focus();
        });
      }
    },
    [isDayDisabled, onChange],
  );

  // Keyboard navigation within the grid (arrow keys, Enter, Space, Escape)
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent, dateStr: string, dayOfMonth: number) => {
      let targetDate: Date | null = null;

      switch (e.key) {
        case "ArrowLeft":
          targetDate = new Date(viewedYear, viewedMonth, dayOfMonth - 1);
          break;
        case "ArrowRight":
          targetDate = new Date(viewedYear, viewedMonth, dayOfMonth + 1);
          break;
        case "ArrowUp":
          targetDate = new Date(viewedYear, viewedMonth, dayOfMonth - 7);
          break;
        case "ArrowDown":
          targetDate = new Date(viewedYear, viewedMonth, dayOfMonth + 7);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          handleDayClick(dateStr);
          return;
        case "Escape":
          if (!inline) {
            e.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
          }
          return;
        default:
          return;
      }

      e.preventDefault();

      if (targetDate) {
        // If the target date is in a different month, navigate
        const targetYearMonth = targetDate.getFullYear() * 12 + targetDate.getMonth();
        const viewedYearMonth = viewedYear * 12 + viewedMonth;
        if (targetYearMonth < viewedYearMonth) {
          if (isAtEarliestMonth) return;
          goToPreviousMonth();
        } else if (targetYearMonth > viewedYearMonth) {
          if (isAtLatestMonth) return;
          goToNextMonth();
        }
        const targetStr = toDateString(targetDate);
        focusedDateRef.current = targetStr;
      }
    },
    [
      viewedYear,
      viewedMonth,
      handleDayClick,
      goToPreviousMonth,
      goToNextMonth,
      isAtEarliestMonth,
      isAtLatestMonth,
      inline,
    ],
  );

  // After render, focus the correct day button when the calendar is open
  useEffect(() => {
    if (isOpen && !inline && monthLabels.long && focusedDateRef.current && gridRef.current) {
      const btn = gridRef.current.querySelector<HTMLButtonElement>(`[data-date="${focusedDateRef.current}"]`);
      btn?.focus();
    }
  }, [isOpen, monthLabels.long, inline]);

  // Focus into the calendar grid when it opens
  useEffect(() => {
    if (isOpen && !inline && gridRef.current) {
      const dateToFocus = value || toDateString(new Date());
      focusedDateRef.current = dateToFocus;
      requestAnimationFrame(() => {
        const btn = gridRef.current?.querySelector<HTMLButtonElement>(`[data-date="${dateToFocus}"]`);
        if (btn) {
          btn.focus();
        } else {
          // If the target date is not in the current month view, focus the first available day
          const firstBtn = gridRef.current?.querySelector<HTMLButtonElement>("button[data-date]:not(:disabled)");
          firstBtn?.focus();
        }
      });
    }
  }, [isOpen, value, inline]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen || inline) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, inline]);

  // Focus trap: keep Tab/Shift+Tab within the popover when open
  useEffect(() => {
    if (!isOpen || inline || !popoverRef.current) return;

    const popover = popoverRef.current;

    const handleTrapKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements = popover.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)',
      );
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    popover.addEventListener("keydown", handleTrapKeyDown);
    return () => {
      popover.removeEventListener("keydown", handleTrapKeyDown);
    };
  }, [isOpen, inline]);

  // Close on Escape from anywhere within the container
  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape" && !inline) {
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    },
    [inline],
  );

  const toggleCalendar = useCallback(() => {
    if (inline) return;
    setIsOpen((prev) => !prev);
  }, [inline]);

  const describedBy = [error ? `${id}-error` : undefined, ariaDescribedBy].filter(Boolean).join(" ") || undefined;

  // Format the selected date for display
  const displayValue = value ? formatDateForDisplay(value) : "";

  return (
    <div className="gcw:w-full gcw:relative" ref={containerRef}>
      <label className={`gcw:block gcw:mb-2 ${labelClassName}`} htmlFor={`${id}-trigger`} id={`${id}-label`}>
        {label}
        {required && <RequiredIndicator />}
      </label>

      {/* Trigger button — styled like a text input (hidden when inline) */}
      {!inline && (
        <button
          ref={triggerRef}
          type="button"
          id={`${id}-trigger`}
          className={`container-inputs gcw:text-left gcw:cursor-pointer gcw:!flex gcw:items-center gcw:justify-between ${error ? "gcw:border-error" : ""}`}
          onClick={toggleCalendar}
          onKeyDown={handleContainerKeyDown}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={`${id}-calendar`}
          aria-describedby={describedBy}
          aria-labelledby={`${id}-label`}
        >
          <span className={`gcw:text-body gcw:text-text-primary`}>
            {displayValue || t("booking_select_date", "Select a date...")}
          </span>
          <Calendar size={20} className="gcw:text-text-primary gcw:shrink-0 gcw:me-2" aria-hidden="true" />
        </button>
      )}

      {/* Calendar popover (or inline grid) */}
      {(inline || isOpen) && (
        <dialog
          ref={popoverRef}
          id={`${id}-calendar`}
          open
          aria-label={`${label} calendar`}
          aria-modal={!inline}
          className={
            inline
              ? "gcw:w-full gcw:bg-white gcw:border gcw:border-light-grey gcw:rounded-md gcw:px-2 gcw:pb-2"
              : "gcw:absolute gcw:z-10 gcw:mt-1 gcw:w-full gcw:md:w-2/4 gcw:bg-white gcw:border gcw:border-medium-grey gcw:rounded-md gcw:shadow-lg gcw:px-2"
          }
        >
          {/* Month navigation header */}
          <div className="gcw:flex gcw:items-center gcw:justify-between gcw:px-2 gcw:py-1.5 gcw:border-b gcw:border-medium-grey">
            <button
              type="button"
              onClick={goToPreviousMonth}
              disabled={isAtEarliestMonth}
              aria-label="Previous month"
              className={getMonthNavClasses(isAtEarliestMonth)}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <span
              className="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              aria-live="polite"
              aria-label={monthLabels.long}
              id={`${id}-month-label`}
            >
              {useCompactLabels ? monthLabels.short : monthLabels.long}
            </span>
            <button
              type="button"
              onClick={goToNextMonth}
              disabled={isAtLatestMonth}
              aria-label="Next month"
              className={getMonthNavClasses(isAtLatestMonth)}
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>

          {/* Calendar grid */}
          <table ref={gridRef} id={`${id}-grid`} aria-labelledby={`${id}-month-label`} className="gcw:w-full">
            <thead>
              <tr className="gcw:grid gcw:grid-cols-7">
                {weekdayLabels.map((weekday) => (
                  <th
                    key={weekday.long}
                    scope="col"
                    className="gcw:py-1 gcw:text-center gcw:text-body gcw:font-medium gcw:text-text-muted"
                    aria-label={weekday.long}
                  >
                    {useCompactLabels ? weekday.narrow : weekday.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => {
                const weekKey = week.find((c) => c !== null)?.dateStr ?? "empty";
                return (
                  <tr key={`week-${weekKey}`} className="gcw:grid gcw:grid-cols-7">
                    {weekdayLabels.map((weekday, weekdayIndex) => {
                      const cell = week[weekdayIndex];

                      if (!cell) {
                        return <td key={`empty-${weekKey}-${weekday.long}`} className="gcw:p-0" />;
                      }

                      const { dateStr, dayOfMonth } = cell;
                      const disabled = isDayDisabled(dateStr);
                      const selected = value === dateStr;
                      const isToday = dateStr === toDateString(new Date());
                      const reason = disabled ? getDisabledReason(dateStr) : undefined;

                      const reasonDetail = reason ? `: ${reason}` : "";
                      const disabledSuffix = disabled ? `, unavailable${reasonDetail}` : "";

                      return (
                        <td key={dateStr} className="gcw:p-0 gcw:text-center">
                          <button
                            type="button"
                            data-date={dateStr}
                            disabled={disabled}
                            onClick={() => handleDayClick(dateStr)}
                            onKeyDown={(e) => handleGridKeyDown(e, dateStr, dayOfMonth)}
                            tabIndex={selected || (!value && isToday) ? 0 : -1}
                            aria-pressed={selected}
                            aria-disabled={disabled}
                            aria-label={`${dayOfMonth} ${monthLabels.long}${disabledSuffix}`}
                            title={reason}
                            className={getDayButtonClasses(selected, disabled, isToday)}
                          >
                            {dayOfMonth}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </dialog>
      )}

      {/* Hidden input for react-hook-form */}
      <input type="hidden" id={id} name={id} value={value} aria-hidden="true" tabIndex={-1} />

      {error && (
        <div id={`${id}-error`} className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
};

// ─── Helpers ───

interface CalendarCell {
  dateStr: string;
  dayOfMonth: number;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Format a YYYY-MM-DD string into a human-readable display format.
 * e.g. "2026-03-22" → "22 March 2026"
 */
function formatDateForDisplay(dateStr: string): string {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;

  const year = parts[0];
  const monthIndex = Number(parts[1]) - 1;
  const day = Number(parts[2]);

  const monthName = MONTH_NAMES[monthIndex];
  if (!monthName) return dateStr;

  return `${day} ${monthName} ${year}`;
}

/** Whether the viewed month is the same as, or earlier than, the month holding `boundary`. */
function isMonthAtOrBefore(year: number, month: number, boundary: string): boolean {
  const [boundaryYear, boundaryMonth] = parseYearMonth(boundary);
  if (boundaryYear === null || boundaryMonth === null) return false;
  return year * 12 + month <= boundaryYear * 12 + boundaryMonth;
}

/** Whether the viewed month is the same as, or later than, the month holding `boundary`. */
function isMonthAtOrAfter(year: number, month: number, boundary: string): boolean {
  const [boundaryYear, boundaryMonth] = parseYearMonth(boundary);
  if (boundaryYear === null || boundaryMonth === null) return false;
  return year * 12 + month >= boundaryYear * 12 + boundaryMonth;
}

/** Pull the year and 0-indexed month out of a YYYY-MM-DD string. */
function parseYearMonth(dateStr: string): [number, number] | [null, null] {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return [null, null];
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  if (!Number.isInteger(year) || !Number.isInteger(month)) return [null, null];
  return [year, month];
}

/**
 * Compute Tailwind classes for a month navigation button.
 */
function getMonthNavClasses(disabled: boolean): string {
  const base = "gcw:p-1 gcw:rounded-md gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary";
  return disabled
    ? `${base} gcw:text-text-light gcw:cursor-not-allowed`
    : `${base} gcw:cursor-pointer gcw:hover:bg-medium-grey`;
}

/**
 * Build the calendar grid for a given month.
 */
function buildCalendarGrid(year: number, month: number, firstDay: Weekday): (CalendarCell | null)[][] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstOfMonth = new Date(year, month, 1);
  // Convert JS day (0=Sun) to a first-weekday-relative offset.
  const startOffset = (firstOfMonth.getDay() - JS_DAY[firstDay] + 7) % 7;

  const weeks: (CalendarCell | null)[][] = [];
  let currentWeek: (CalendarCell | null)[] = new Array(startOffset).fill(null) as null[];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    currentWeek.push({
      dateStr: toDateString(date),
      dayOfMonth: day,
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Pad the last week with nulls
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

/**
 * Compute Tailwind classes for a day button.
 */
function getDayButtonClasses(selected: boolean, disabled: boolean, isToday: boolean): string {
  const base =
    "gcw:w-full gcw:aspect-square gcw:flex gcw:items-center gcw:justify-center gcw:text-body gcw:rounded-md gcw:transition-colors gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1";

  if (disabled) {
    return `${base} gcw:text-text-light gcw:cursor-not-allowed gcw:bg-transparent`;
  }

  if (selected) {
    return `${base} gcw:bg-primary gcw:text-white gcw:font-semibold gcw:cursor-pointer gcw:hover:bg-primary-hover`;
  }

  if (isToday) {
    return `${base} booking-calendar-day-today gcw:bg-medium-grey gcw:text-text-primary gcw:font-bold gcw:cursor-pointer gcw:hover:bg-medium-grey`;
  }

  return `${base} gcw:text-text-primary gcw:cursor-pointer gcw:hover:bg-medium-grey`;
}

export default CalendarDatePicker;
